import { z } from 'zod';

import {
    AUTOSHIP_FREQUENCY_VALUES,
    AUTOSHIP_ONE_TIME_OPTION,
    AUTOSHIP_UNIT_OF_MEASURE_VALUES,
} from '@libs/common-types';

const AUTOSHIP_FORM_UNIT_VALUES = [AUTOSHIP_ONE_TIME_OPTION.value, ...AUTOSHIP_UNIT_OF_MEASURE_VALUES] as const;
const AUTOSHIP_FORM_FREQUENCY_VALUES = [AUTOSHIP_ONE_TIME_OPTION.value, ...AUTOSHIP_FREQUENCY_VALUES] as const;

const lineBaseSchema = z.object({
    order: z.number().int().positive(),
    text: z.string(),
    max: z.number().int().positive(),
    required: z.boolean(),
});

const NON_ASCII_PRINTABLE_CHARS_REGEX = /[^\x20-\x7E]+/;

const lineSchema = lineBaseSchema.superRefine((value, ctx) => {
    // 1) Max length
    if (value.text.length > value.max) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Max ${value.max} symbols`,
            path: ['text'],
        });
    }

    // 2) Requirement first line
    if (value.required && value.text.trim() === '') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Line ${value.order} is required`,
            path: ['text'],
        });
    }

    // 3) Standard US keyboard printable chars only
    if (value.text.length > 0 && NON_ASCII_PRINTABLE_CHARS_REGEX.test(value.text)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Special characters not supported',
            path: ['text'],
        });
    }
});

export const formSchema = z.object({
    quantity: z.number().min(1),
    purchaseOption: z.enum(['one-time', 'autoship']),
    subscriptionType: z.enum(['new', 'existing']),
    autoshipUnit: z.enum(AUTOSHIP_FORM_UNIT_VALUES),
    autoshipFrequency: z.enum(AUTOSHIP_FORM_FREQUENCY_VALUES),
    fulfilmentMethod: z.enum(['bopis', 'sdd', 'sth']),
    personalization: z.object({
        colorStyle: z.string().optional(),
        fontFace: z.string().optional(),
        frontLines: z.array(lineSchema).optional(),
        backLines: z.array(lineSchema).optional(),
    }),
});

export type FormFields = z.infer<typeof formSchema>;
